/* eslint-disable max-lines */
/* eslint-disable no-bitwise */
/**
 * Deterministic mock of the bank dossier.
 *
 * Until the API returns `WorkOrderData.bank_dossier`, this derives a full
 * dossier from what the work order already carries (client, company, dates,
 * price, attachments) and fills the rest with values seeded by the OS id, so
 * the same OS always shows the same data and every screen state can be
 * reviewed. Delete this file once the backend ships the field.
 */
import {
  channelOptions,
  contactTypeOptions,
  creditLinePurposeOptions,
  eligibilityOptions,
  operationTypeOptions,
  unitTypeOptions,
} from '../../../constants/bankDossier';
import { ArchivedReason, PropertyType, Status } from '../../enumerations';
import { WorkOrderData } from '../types';
import {
  BankCharge,
  BankContact,
  BankDossier,
  BankFile,
  BankMilestones,
  BankRelatedRequest,
  bankMilestoneKeys,
} from './types';

const DAY = 24 * 60 * 60 * 1000;

const firstNames = [
  'Ana',
  'Bruno',
  'Carla',
  'Diego',
  'Fernanda',
  'Gustavo',
  'Helena',
  'Igor',
  'Juliana',
  'Lucas',
  'Mariana',
  'Rafael',
  'Sérgio',
  'Tatiane',
];
const lastNames = [
  'Almeida',
  'Barbosa',
  'Campos',
  'Duarte',
  'Ferreira',
  'Gonçalves',
  'Lima',
  'Moraes',
  'Nogueira',
  'Oliveira',
  'Ribeiro',
  'Souza',
];
const condominiums = [
  'Residencial Jardim das Acácias',
  'Edifício Solar da Colina',
  'Condomínio Parque dos Ipês',
  'Torres do Horizonte',
  'Ville de France',
];
const departments = [
  'Engenharia de Avaliações',
  'Gestão de Garantias',
  'Crédito Imobiliário – Engenharia',
];
const additionalInfos = [
  'Vistoria realizada com acompanhamento do proponente.',
  'Documentação complementar recebida por e-mail.',
  'Imóvel com obra de fachada em andamento no condomínio.',
];

/** mulberry32 — small, fast, deterministic. */
const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

interface Rng {
  next: () => number;
  int: (min: number, max: number) => number;
  pick: <T>(items: T[]) => T;
  chance: (probability: number) => boolean;
  digits: (length: number) => string;
  name: () => string;
}

const createRng = (seed: number): Rng => {
  const next = createRandom(seed);
  const int = (min: number, max: number): number =>
    Math.floor(next() * (max - min + 1)) + min;
  const pick = <T>(items: T[]): T => items[int(0, items.length - 1)];
  return {
    next,
    int,
    pick,
    chance: (probability) => next() < probability,
    digits: (length) =>
      Array.from({ length }, () => int(0, 9).toString()).join(''),
    name: () => `${pick(firstNames)} ${pick(lastNames)} ${pick(lastNames)}`,
  };
};

const roundTo = (value: number, step: number): number =>
  Math.round(value / step) * step;

const shift = (iso: string | null | undefined, days: number): string | null => {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  return new Date(time + days * DAY).toISOString();
};

const isoOrNull = (value: string | null | undefined): string | null =>
  value || null;

const apartmentTypes = new Set<PropertyType>([
  PropertyType.APARTMENT,
  PropertyType.COMMERCIAL_SPACE,
]);

const avmValue = (workOrder: WorkOrderData): number | null => {
  const report = workOrder.avm_report;
  if (!report) return null;
  if ('rounded_market_value' in report) return report.rounded_market_value;
  if ('aggregated_homogenization' in report) {
    return report.aggregated_homogenization.rounded_market_value;
  }
  return null;
};

const streetTypeFrom = (street: string | null | undefined): string => {
  const first = (street ?? '').trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    rua: 'R',
    r: 'R',
    'r.': 'R',
    avenida: 'Av',
    av: 'Av',
    'av.': 'Av',
    alameda: 'Al',
    al: 'Al',
    travessa: 'Tv',
    estrada: 'Est',
    rodovia: 'Rod',
    praça: 'Pç',
    praca: 'Pç',
  };
  return map[first] ?? 'R';
};

const floorFrom = (complement: string | null | undefined): string | null => {
  const match = (complement ?? '').match(/(\d{1,2})\s*[ºo°]?\s*andar/i);
  return match ? match[1] : null;
};

const buildMilestones = (
  workOrder: WorkOrderData,
  rng: Rng,
  ordinal: number
): BankMilestones => {
  const milestones = Object.fromEntries(
    bankMilestoneKeys.map((key) => [key, null])
  ) as BankMilestones;
  const { status, hive_request: request, inspection } = workOrder;
  const created = workOrder.created_at;
  const bankDue =
    isoOrNull(workOrder.limit_finish_at) ??
    shift(created, workOrder.deadline_in_days || 10);

  milestones.created_at = created;
  milestones.bank_due_date = bankDue;
  milestones.report_delivery_date = bankDue;
  milestones.appraisal_company_due_date =
    isoOrNull(request?.complete_until) ?? shift(bankDue, -1);
  milestones.assignment_date =
    isoOrNull(request?.created_at) ??
    (status >= Status.SCHEDULE ? shift(created, 1) : null);
  milestones.acceptance_date =
    isoOrNull(request?.accepted_at) ??
    (status >= Status.SCHEDULE ? shift(created, 1) : null);
  milestones.engineer_assignment_date =
    status >= Status.SCHEDULE ? shift(milestones.acceptance_date, 1) : null;
  milestones.probable_inspection_date = isoOrNull(inspection?.schedule_at);
  milestones.inspection_date =
    isoOrNull(inspection?.start_at) ??
    (status >= Status.SAMPLE_CREATION
      ? isoOrNull(inspection?.schedule_at)
      : null);
  milestones.report_completion_date =
    isoOrNull(request?.worker_finished_at) ??
    (status >= Status.REVISION ? shift(bankDue, -1) : null);
  milestones.report_received_at =
    isoOrNull(request?.hive_finished_at) ??
    (status === Status.ARCHIVED ? shift(bankDue, 0) : null);

  if (workOrder.archived_reason === ArchivedReason.CANCELED) {
    milestones.cancellation_request_date = shift(created, rng.int(1, 4));
    milestones.cancellation_date = shift(
      milestones.cancellation_request_date,
      1
    );
  }
  if (status === Status.REVISION) {
    milestones.revision_request_date = shift(
      milestones.report_completion_date ?? created,
      1
    );
  }
  if (ordinal > 1) {
    milestones.rectification_request_date = shift(created, 0);
    if (status === Status.ARCHIVED) {
      milestones.rectification_closed_date = shift(created, rng.int(2, 6));
    }
  }
  return milestones;
};

const buildCharges = (
  workOrder: WorkOrderData,
  rng: Rng,
  ordinal: number
): BankDossier['charges'] => {
  const appraiserFee =
    workOrder.hive_request?.price ?? roundTo(rng.int(800, 2600), 10);
  const bankCharged = roundTo(appraiserFee * 1.25, 10);
  const created = workOrder.created_at;
  const items: BankCharge[] = [
    {
      id: 'c-bank',
      fee_type: 'Valor Cobrado do Banco',
      charge_type: 'Laudo',
      bank_charged_value: bankCharged,
      appraiser_fee: null,
      platform_fee: bankCharged - appraiserFee,
      created_at: created,
      notes: 'Atribuição de Solicitação de Avaliação',
    },
    {
      id: 'c-fee',
      fee_type: 'Honorário do Avaliador',
      charge_type: 'Laudo',
      bank_charged_value: null,
      appraiser_fee: appraiserFee,
      platform_fee: null,
      created_at: created,
      notes: 'Atribuição de Solicitação de Avaliação',
    },
  ];
  if (ordinal > 1) {
    const rectification = roundTo(appraiserFee * 0.3, 10);
    items.push({
      id: 'c-rect',
      fee_type: 'Retificação',
      charge_type: 'Retificação',
      bank_charged_value: rectification,
      appraiser_fee: roundTo(rectification * 0.8, 10),
      platform_fee: roundTo(rectification * 0.2, 10),
      created_at: shift(created, 1) ?? created,
      notes: `${ordinal}ª solicitação da pasta`,
    });
  }
  return {
    items,
    total_bank_charges: items.reduce(
      (sum, item) => sum + (item.bank_charged_value ?? 0),
      0
    ),
    total_fees: items.reduce((sum, item) => sum + (item.appraiser_fee ?? 0), 0),
  };
};

const buildContacts = (
  workOrder: WorkOrderData,
  rng: Rng,
  proponent: string
): BankContact[] => {
  const contacts: BankContact[] = [];
  const phone = (): string => `11${rng.digits(9)}`;
  const landline = (): string => `11${rng.digits(8)}`;
  const emailFor = (name: string, domain: string): string =>
    `${name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .split(/\s+/)
      .slice(0, 2)
      .join('.')}@${domain}`;

  contacts.push({
    id: 'k-proponent',
    contact_type: 'Proponente',
    full_name: proponent,
    organization: null,
    contact_code: rng.digits(6),
    email: emailFor(proponent, 'email.com'),
    business_phone: null,
    extension: null,
    home_phone: rng.chance(0.4) ? landline() : null,
    mobile_phone: phone(),
    fax: null,
  });
  if (workOrder.vvm_responsible) {
    contacts.push({
      id: 'k-bank',
      contact_type: 'Analista do banco',
      full_name: workOrder.vvm_responsible,
      organization: workOrder.client_name,
      contact_code: rng.digits(6),
      email: workOrder.vvm_responsible_email,
      business_phone:
        workOrder.vvm_responsible_phone_number?.replace(/\D/g, '') ?? null,
      extension: rng.digits(4),
      home_phone: null,
      mobile_phone: null,
      fax: null,
    });
  }
  if (workOrder.inspection?.responsible) {
    contacts.push({
      id: 'k-inspection',
      contact_type: 'Responsável pela vistoria',
      full_name: workOrder.inspection.responsible,
      organization: null,
      contact_code: null,
      email: null,
      business_phone: null,
      extension: null,
      home_phone: null,
      mobile_phone:
        workOrder.inspection.responsible_phone?.replace(/\D/g, '') ?? phone(),
      fax: null,
    });
  }
  if (rng.chance(0.6)) {
    const type = rng.pick(
      contactTypeOptions.filter(
        (option) =>
          ![
            'Proponente',
            'Analista do banco',
            'Responsável pela vistoria',
          ].includes(option)
      )
    );
    const name = rng.name();
    contacts.push({
      id: 'k-extra',
      contact_type: type,
      full_name: name,
      organization:
        type === 'Corretor' ? 'Imobiliária Horizonte' : workOrder.client_name,
      contact_code: rng.digits(6),
      email: emailFor(
        name,
        type === 'Corretor' ? 'horizonte.com.br' : 'banco.com.br'
      ),
      business_phone: landline(),
      extension: rng.chance(0.5) ? rng.digits(3) : null,
      home_phone: null,
      mobile_phone: phone(),
      fax: rng.chance(0.2) ? landline() : null,
    });
  }
  return contacts;
};

interface FileSeed {
  type: string;
  name: string;
  present: boolean;
  url: string | null;
  size: number;
  daysAfter: number;
}

const buildFiles = (
  workOrder: WorkOrderData,
  rng: Rng,
  control: string,
  related: BankRelatedRequest[]
): BankFile[] => {
  const seeds: FileSeed[] = [
    {
      type: 'Matrícula',
      name: 'Matricula.pdf',
      present: Boolean(workOrder.real_estate_registry),
      url: workOrder.real_estate_registry,
      size: rng.int(800_000, 3_400_000),
      daysAfter: 0,
    },
    {
      type: 'IPTU',
      name: 'IPTU.pdf',
      present: Boolean(workOrder.iptu_document),
      url: workOrder.iptu_document,
      size: rng.int(120_000, 400_000),
      daysAfter: 0,
    },
    {
      type: 'Fachada',
      name: 'Fachada.jpg',
      present: Boolean(workOrder.facade_image),
      url: workOrder.facade_image || null,
      size: rng.int(400_000, 2_000_000),
      daysAfter: 2,
    },
    {
      type: 'Cálculos Avaliatórios',
      name: 'Calculos_Avaliatorios.xlsx',
      present: Boolean(workOrder.report_xlsx),
      url: workOrder.report_xlsx,
      size: rng.int(200_000, 600_000),
      daysAfter: 5,
    },
    {
      type: 'Laudo',
      name: `Laudo_OS_${workOrder.reference_number}.pdf`,
      present:
        Boolean(workOrder.report_pdf) || workOrder.status >= Status.REPORT,
      url: workOrder.report_pdf,
      size: rng.int(400_000, 1_200_000),
      daysAfter: 5,
    },
  ];

  const own: BankFile[] = seeds
    .filter((seed) => seed.present)
    .map((seed, index) => ({
      id: `f-${workOrder.id}-${index}`,
      file_type: seed.type,
      file_name: seed.name,
      size_bytes: seed.size,
      created_at:
        shift(workOrder.created_at, seed.daysAfter) ?? workOrder.created_at,
      bank_control_number: control,
      work_order_id: workOrder.id,
      work_order_reference: workOrder.reference_number,
      url: seed.url,
      is_latest_report: seed.type === 'Laudo',
    }));

  const folder: BankFile[] = related
    .filter((item) => item.work_order_id === null)
    .flatMap((item, index) => [
      {
        id: `f-rel-${index}-laudo`,
        file_type: 'Laudo',
        file_name: `Laudo_${item.bank_control_number}.pdf`,
        size_bytes: rng.int(400_000, 1_200_000),
        created_at: shift(item.created_at, 4) ?? item.created_at,
        bank_control_number: item.bank_control_number,
        work_order_id: -(index + 1),
        work_order_reference: item.reference_number ?? 0,
        url: null,
        is_latest_report: false,
      },
    ]);

  return [...own, ...folder];
};

const buildRelated = (
  workOrder: WorkOrderData,
  rng: Rng,
  ordinal: number,
  folder: string
): BankRelatedRequest[] => {
  if (ordinal <= 1) return [];
  const company = workOrder.worker_company?.name ?? 'Empresa credenciada';
  return Array.from({ length: ordinal - 1 }, (_, index) => ({
    work_order_id: null,
    reference_number: null,
    bank_control_number: `${folder.replace('-', '')}${index + 1}`.slice(-13),
    request_ordinal: index + 1,
    appraisal_company: company,
    report_type: 'Laudo Eletrônico',
    status: null,
    status_label: index + 1 < ordinal ? 'Finalizado' : 'Em andamento',
    created_at:
      shift(workOrder.created_at, -(ordinal - index) * 12) ??
      workOrder.created_at,
  }));
};

const cache = new Map<number, BankDossier>();

export const getBankDossier = (workOrder: WorkOrderData): BankDossier => {
  const cached = cache.get(workOrder.id);
  if (cached) return cached;

  const rng = createRng(workOrder.id * 7919 + 17);
  const created = new Date(workOrder.created_at);
  const validCreated = !Number.isNaN(created.getTime());
  const ymd = validCreated
    ? `${created.getFullYear()}${String(created.getMonth() + 1).padStart(
        2,
        '0'
      )}${String(created.getDate()).padStart(2, '0')}`
    : '20260101';

  const control = `PV${rng.digits(11)}`;
  const folder = `${ymd}-${rng.digits(4)}`;
  const ordinal = rng.chance(0.25) ? rng.int(2, 3) : 1;
  const isApartment = apartmentTypes.has(workOrder.real_estate_kind);

  const propertyValue = roundTo(rng.int(250_000, 2_500_000), 5_000);
  const financingValue = roundTo(
    propertyValue * (0.5 + rng.next() * 0.3),
    1_000
  );
  const purchaseValue = roundTo(
    propertyValue * (0.95 + rng.next() * 0.1),
    1_000
  );
  const ltv = Math.round((financingValue / propertyValue) * 10_000) / 100;
  const proponent = workOrder.client_name?.trim() || rng.name();
  const appraisalValue =
    avmValue(workOrder) ??
    (workOrder.status >= Status.REPORT
      ? roundTo(propertyValue * (0.9 + rng.next() * 0.2), 1_000)
      : null);

  const subStatusByStatus: Partial<Record<Status, string | null>> = {
    [Status.ENTRANCE]: rng.chance(0.5) ? 'Aguardando documentação' : null,
    [Status.PEPT]: 'Pendência técnica',
    [Status.REVISION]: 'Revisão solicitada',
    [Status.ARCHIVED]:
      workOrder.archived_reason === ArchivedReason.CANCELED
        ? 'Cancelado pelo banco'
        : 'Laudo recebido',
  };

  const related = buildRelated(workOrder, rng, ordinal, folder);
  let requestKind = 'Isolado';
  if (ordinal > 1) requestKind = 'Retificação';
  else if (rng.chance(0.15)) requestKind = 'Lote';

  const dossier: BankDossier = {
    identification: {
      bank_control_number: control,
      folder_number: folder,
      request_number: rng.chance(0.6) ? rng.digits(7) : null,
      request_ordinal: ordinal,
      sub_status: subStatusByStatus[workOrder.status] ?? null,
      channel: rng.pick(channelOptions),
    },
    financing: {
      operation_type: rng.pick(operationTypeOptions),
      request_kind: requestKind,
      purchase_sale_value: purchaseValue,
      financing_value: financingValue,
      ltv,
      property_value: propertyValue,
      original_proposal_number: rng.digits(9),
      previous_proposal_number: rng.chance(0.3) ? rng.digits(9) : null,
      property_owner: rng.chance(0.7) ? rng.name() : proponent,
      proponent_name: proponent,
      proponent_document: rng.digits(11),
      uses_fgts: rng.chance(0.4),
      proponent_eligibility: rng.pick(eligibilityOptions),
      credit_line_purpose: rng.pick(creditLinePurposeOptions),
    },
    appraisal_value: appraisalValue,
    milestones: buildMilestones(workOrder, rng, ordinal),
    appraisal_company: {
      escalated_engineer: rng.chance(0.3) ? `Eng. ${rng.name()}` : null,
      revision_type:
        workOrder.status === Status.REVISION ? 'Revisão técnica' : null,
      revision_responsible:
        workOrder.status === Status.REVISION ? `Eng. ${rng.name()}` : null,
      additional_info: rng.chance(0.5) ? rng.pick(additionalInfos) : null,
    },
    requesting_bank: {
      engineering_department: rng.pick(departments),
      area_analyst: workOrder.vvm_responsible || rng.name(),
      manager_name: rng.name(),
      requires_bank_engineering_analysis: rng.chance(0.5),
    },
    address_extras: {
      street_type: streetTypeFrom(workOrder.street),
      floor:
        floorFrom(workOrder.address_complement) ??
        (isApartment ? String(rng.int(1, 22)) : null),
      condominium_name: isApartment ? rng.pick(condominiums) : null,
      original_address: [
        workOrder.street,
        workOrder.number,
        workOrder.address_complement,
        workOrder.district,
        workOrder.city,
        workOrder.state,
        workOrder.zip_code,
      ]
        .filter((part) => part && String(part).trim().length > 0)
        .join(', ')
        .toUpperCase(),
      is_new_area: rng.chance(0.08),
    },
    registry_extras: {
      total_linked_units: isApartment ? rng.int(24, 320) : null,
      linked_units: isApartment
        ? Array.from({ length: rng.int(1, 3) }, (_, index) => ({
            id: `u-${index}`,
            registry_number: `${rng.digits(3)}.${rng.digits(3)}`,
            quantity: index === 0 ? 1 : rng.int(1, 2),
            unit_type: index === 0 ? 'Apartamento' : rng.pick(unitTypeOptions),
            description:
              index === 0
                ? `Unidade ${workOrder.address_complement || rng.int(11, 184)}`
                : `Vaga ${rng.int(1, 120)}`,
          }))
        : [],
    },
    characteristics_extras: {
      blocks_towers: isApartment ? rng.int(1, 6) : null,
      is_new_construction: workOrder.age === 0 || rng.chance(0.15),
      is_rural: rng.chance(0.05),
    },
    report_kind: {
      original_report_reference:
        ordinal > 1
          ? `Laudo ${related[related.length - 1]?.bank_control_number}`
          : null,
    },
    charges: buildCharges(workOrder, rng, ordinal),
    contacts: buildContacts(workOrder, rng, proponent),
    files: buildFiles(workOrder, rng, control, related),
    related_requests: related,
  };

  cache.set(workOrder.id, dossier);
  return dossier;
};
